import React, { useCallback, useDebugValue, useEffect, useState } from 'react';
import useAuth from '@vl-core/useAuth';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import { Modal, Input } from 'antd';
import { useRegistrationFormInitialValues } from '@components/Patients/Registration/utils/useRegistrationFormInitialValues';
import useUser from '@vl-core/hooks/useUser';
import { useRegistrationFormInstance } from '@components/Patients/Registration/useSelfRegistrationFormInstance';
import * as actions from '@actions';
import useRouter from '@vl-core/useRouter';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useCase from '@hooks/useCase';
import styled from '@emotion/styled';
import { formatPracticeFromObject } from '../../../pages/patients/profile';
import { RegistrationForm } from '../Registration/RegistrationForm';
import { DocmanConsentQuestions } from './DocmanConsentQuestions';

export const useUsersGP = () => {
  const { user_guid, restricted } = useUser();
  const dispatch = useDispatch();

  const { data } = useQuery({
    queryKey: ['getGPDetails', user_guid],
    queryFn: () => dispatch(actions.getGpDetails(user_guid)),
    enabled: !restricted
  });

  const gpDetails = (data && 'payload' in data ? data.payload : data) as typeof data;
  useDebugValue(gpDetails);

  return gpDetails;
};

const useCaseDetails = (caseId: number) => {
  const [caseDetails, refetch, isLoading] = useCase(caseId);

  return { caseDetails, invalidateCaseDetails: refetch, isLoading };
};

// returns true if and only if the user has a GP and that GP accepts Docman
export const usePatientsGPAcceptsDocman = () => {
  const gp = useUsersGP();

  return !!gp?.docman_active;
};

interface GetDocmanConsentProps {
  caseName?: string;
  caseId: number;
  surveyjsId: string | undefined;
  userGuid: string | undefined;
  onCancel?: () => void;
  setVisibility?: (value: boolean) => void;
  isOpen: boolean;
}

const CaseDetailsBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  label {
    font-weight: bold;
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  input:disabled {
    color: inherit;
  }
`;

export const GetDocmanConsent = ({
  caseName,
  caseId,
  surveyjsId,
  userGuid,
  onCancel,
  setVisibility,
  isOpen
}: GetDocmanConsentProps) => {
  const [displayModal, setDisplayModal] = useState(true);
  const form = useRegistrationFormInstance();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { user_guid } = useUser();
  const { invalidateCaseDetails, caseDetails } = useCaseDetails(caseId);

  const invalidateUsersGPDetails = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['getGPDetails', user_guid]
    });
  }, [queryClient]);

  const { mutate: postDocmanProfileValues, isPending } = useMutation({
    mutationFn: async ({
      docmanConsent,
      docmanConsentRequired
    }: {
      docmanConsent: number;
      docmanConsentRequired: number;
    }) => {
      if (!userGuid || !surveyjsId) throw new Error('Missing user or survey ID');

      const docmanConsentRes = await dispatch(
        actions.postCaseDocmanConsent(surveyjsId, userGuid, docmanConsent, docmanConsentRequired)
      );

      const request = form.getFieldsValue(true);
      const profileRes = await dispatch(
        actions.updateDetails({
          ...request,
          nhs_number: form.getFieldValue('nhs_number'),
          gp_ods_code: form.getFieldValue('gp_ods_code')
        })
      );
      return { docmanConsentRes, profileRes };
    },
    onSuccess: () => {
      if (setVisibility !== undefined) {
        setVisibility(false);
      }
      setDisplayModal(false);
      invalidateCaseDetails();
      invalidateUsersGPDetails();
    },
    onError: (error) => {
      console.error('Docman or profile request failed', error);
    }
  });

  const handleCancel = () => {
    onCancel?.();
  };

  const validateAndSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const docmanConsent = values.gp_summary_consent ? 1 : 0; // Backend expects 1/0 not true/false
      const docmanConsentRequired = 0;

      postDocmanProfileValues({ docmanConsent, docmanConsentRequired });
    } catch (errorInfo) {
      console.error('Validation failed', errorInfo);
    }
  }, [form]);

  if (!caseDetails) return null;

  return (
    <Modal
      title={<h2>{t('Consent to Share Clinical Records')}</h2>}
      open={!setVisibility ? displayModal && isOpen : isOpen}
      onOk={validateAndSave}
      okText={t('Update')}
      confirmLoading={isPending}
      onCancel={handleCancel}
      width={650}
      zIndex={100000}
      maskClosable={false}
      keyboard={false}
    >
      <p style={{ fontWeight: 'bold' }}>{t('Consent to Share Clinical Records')}</p>
      <p>
        {t(
          'We can automatically share your case documentation with your GP, provided your practice supports electronic sharing. We require your consent to do so.'
        )}
      </p>
      <p>
        {t(
          "We believe it's important that your GP is kept up to date with all of your clinical documentation. This helps ensure better coordination and continuity of your care."
        )}
      </p>
      <p style={{ fontWeight: 'bold' }}>{t('How to Manage Consent')}</p>
      <p>
        {t(
          "We ask for separate consent for each condition you are being treated for. You can update or withdraw your consent by selecting 'Manage Consent' within the 'Documents' tab for each of your cases. "
        )}
      </p>

      <CaseDetailsBlock>
        <div>
          <label htmlFor="consent-case-speciality">{t('Speciality:')}</label>
          <Input value={caseName} id="consent-case-speciality" disabled />
        </div>

        <div>
          <label htmlFor="consent-case-ref">{t('Case Number:')}</label>
          <Input value={caseDetails.client_case_id} id="consent-case-ref" disabled />
        </div>
      </CaseDetailsBlock>
      <DocmanConsentQuestions caseDetails={caseDetails} />
    </Modal>
  );
};

interface GetDocmanConsentFormProps {
  caseName?: string;
  caseId: number;
  surveyjsId: string | undefined;
  userGuid: string | undefined;
  onCancel?: () => void;
  setVisibility?: (value: boolean) => void;
  isOpen: boolean;
}

export const GetDocmanConsentForm = ({
  caseName,
  caseId,
  surveyjsId,
  userGuid,
  onCancel,
  setVisibility,
  isOpen
}: GetDocmanConsentFormProps) => {
  const practice = useUsersGP();
  let initialValues = useRegistrationFormInitialValues();

  if (practice?.gp_ods_code) {
    initialValues = {
      ...initialValues,
      gp_address_preview: formatPracticeFromObject(practice),
      docman_active: practice.docman_active,
      gp_ods_code: practice.gp_ods_code
    };
  }

  if (practice === undefined) return null;

  return (
    <RegistrationForm initialValues={initialValues} layout="vertical">
      <GetDocmanConsent
        caseName={caseName}
        caseId={caseId}
        surveyjsId={surveyjsId}
        userGuid={userGuid}
        onCancel={onCancel}
        setVisibility={setVisibility}
        isOpen={isOpen}
      />
    </RegistrationForm>
  );
};

// conditionally displays a modal asking for Docman consent
export const MaybeGetDocmanConsent = ({
  children,
  surveyjsId,
  caseId,
  isOpen
}: {
  children?: React.ReactNode;
  surveyjsId: string | undefined;
  caseId: number;
  isOpen: boolean;
}) => {
  const { user } = useAuth();
  const { DOCMAN } = useRuntimeConfig();
  const [needsConsent, setNeedsConsent] = useState(false);
  const { pathname } = useRouter();

  useEffect(() => {
    if (!user || !DOCMAN) return;

    const { user_type_code, ask_for_gp_consent, user_subtype_code } = user;

    // check the user is a patient and not restricted
    if (user_type_code !== 'PATIENT' || user_subtype_code === 'RESTRICTED') {
      return;
    }

    // check the patient isn't en-route to their dashboard
    const locationParts = pathname.split('/');
    if (
      ['login', 'policy', 'create-account', 'register', 'linklogin', 'axalogin'].some((url) =>
        locationParts.includes(url)
      )
    ) {
      return;
    }

    setNeedsConsent(Boolean(ask_for_gp_consent));
  }, [user, DOCMAN, pathname]);

  return (
    <>
      {needsConsent && user && (
        <GetDocmanConsentForm caseId={caseId} userGuid={user.user_guid} surveyjsId={surveyjsId} isOpen={isOpen} />
      )}
      {children}
    </>
  );
};
